import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Card,
  CardBody,
  Spinner,
  Alert,
  Table,
} from "reactstrap";
import ComponentCard from "./ComponentCard";
import "../../App.css";
import { getInfAppConfigsAPI, setInfAppConfigsAPI } from "../../service/ManageOtherControlsService";
import RefreshIcon from "@mui/icons-material/Refresh";

const authService = require("../../service/AuthService");

const CONFIG_KEYS = {
  trainingApprovalRequired: "training_approval_required",
  creditPerHour: "credit_per_hour",
};

export default function ManageOtherControls() {
  const sessionUser = authService.getUser?.() || {};
  const defaultOrg = (sessionUser?.organization || "").trim().toLowerCase();
  const loggedInUserId = (sessionUser?.user_name || sessionUser?.username || "").trim();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState({ type: "", msg: "" });

  const [rows, setRows] = useState([]);           // editable rows
  const [originalRows, setOriginalRows] = useState([]); // snapshot
  const [rowEditing, setRowEditing] = useState({}); // { [config_name]: true/false }
  const [tableEditing, setTableEditing] = useState(false);

  const rowKey = (r) => String(r?.config_name || "");

  const preventInvalidIntKeys = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
  };

  const normalizeRowsFromApi = (items = []) => {
    return (items || [])
      .filter((it) => it?.config_name)
      .map((it) => {
        const name = String(it.config_name);

        if (name === CONFIG_KEYS.trainingApprovalRequired) {
          return {
            config_name: name,
            label: "Admin approval for model training",
            type: "bool",
            config_value:
              typeof it.config_value === "boolean" ? it.config_value : false,
          };
        }

        if (name === CONFIG_KEYS.creditPerHour) {
          const n = Number(it.config_value);
          return {
            config_name: name,
            label: "Usage credit per hour",
            type: "number",
            config_value: Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0,
          };
        }

        // ignore unknown configs
        return null;
      })
      .filter(Boolean);
  };


  const isDirty = useMemo(() => {
    if (rows.length !== originalRows.length) return true;
    for (let i = 0; i < rows.length; i++) {
      const a = rows[i];
      const b = originalRows[i];
      if (!b) return true;
      if (a.config_name !== b.config_name) return true;
      if (a.config_value !== b.config_value) return true;
    }
    return false;
  }, [rows, originalRows]);

  const loadConfigs = async () => {
    setStatus({ type: "", msg: "" });
    try {
      setLoading(true);
      const resp = await getInfAppConfigsAPI({
        config_names: [CONFIG_KEYS.trainingApprovalRequired, CONFIG_KEYS.creditPerHour],
      });
      const items = Array.isArray(resp?.items) ? resp.items : [];
      const normalized = normalizeRowsFromApi(items);

      setRows(normalized);
      setOriginalRows(JSON.parse(JSON.stringify(normalized)));
      setRowEditing({});
      setTableEditing(false);

      if (!normalized.length) {
        setStatus({ type: "danger", msg: "No configs returned." });
      }
    } catch (e) {
      setRows([]);
      setOriginalRows([]);
      setStatus({
        type: "danger",
        msg: e?.response?.data?.message || e?.message || "Failed to load app configs.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== editing helpers (same style as ManageUserCredits) =====
  const enableRowEdit = (rk, on) => {
    setRowEditing((prev) => ({ ...prev, [rk]: on }));
  };

  const enableTableEdit = (on) => {
    setTableEditing(on);
    if (on) {
      const all = {};
      rows.forEach((r) => (all[rowKey(r)] = true));
      setRowEditing(all);
    } else {
      setRowEditing({});
      setRows(JSON.parse(JSON.stringify(originalRows)));
    }
  };

  const handleCellChange = (idx, value) => {
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };

      if (r.type === "bool") {
        r.config_value = !!value;
      } else if (r.type === "number") {
        const n = Number(value);
        r.config_value = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      } else {
        r.config_value = value;
      }

      next[idx] = r;
      return next;
    });
  };

  const validateRow = (r) => {
    if (!r?.config_name) return "Missing config_name.";
    if (r.config_name === CONFIG_KEYS.creditPerHour) {
      const n = Number(r.config_value);
      if (!Number.isInteger(n) || n < 0) return "Credit per hour must be a non-negative whole number.";
    }
    if (r.config_name === CONFIG_KEYS.trainingApprovalRequired) {
      if (typeof r.config_value !== "boolean") return "Training approval must be true/false.";
    }
    return "";
  };

  // ===== Row Submit / Cancel =====
  const handleRowCancel = (rk) => {
    const idx = rows.findIndex((r) => rowKey(r) === rk);
    const orig = originalRows.find((r) => rowKey(r) === rk);
    if (idx >= 0 && orig) {
      setRows((prev) => {
        const next = [...prev];
        next[idx] = { ...orig };
        return next;
      });
    }
    enableRowEdit(rk, false);
  };

  const handleRowSubmit = async (rk) => {
    const r = rows.find((x) => rowKey(x) === rk);
    if (!r) return;

    const err = validateRow(r);
    if (err) {
      setStatus({ type: "danger", msg: err });
      return;
    }

    try {
      setSaving(true);
      setStatus({ type: "", msg: "" });

      const resp = await setInfAppConfigsAPI({
        updatedBy: loggedInUserId,
        updates: [{ config_name: r.config_name, config_value: r.config_value }],
      });

      // update local snapshot for this row
      setOriginalRows((prev) => {
        const copy = [...prev];
        const idx = copy.findIndex((x) => rowKey(x) === rk);
        if (idx >= 0) copy[idx] = { ...r };
        return copy;
      });

      enableRowEdit(rk, false);
      setStatus({ type: "success", msg: resp?.message || `Updated ${r.config_name}.` });
    } catch (e) {
      setStatus({
        type: "danger",
        msg: e?.response?.data?.message || e?.message || "Failed to update row.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ===== Table Submit / Cancel =====
  const handleTableCancel = () => {
    enableTableEdit(false);
    setStatus({ type: "", msg: "" });
  };

  const handleTableSubmit = async () => {
    // Collect changed rows only
    const updates = [];
    for (let i = 0; i < rows.length; i++) {
      const a = rows[i];
      const b = originalRows[i];
      if (!b) continue;

      const changed = a.config_value !== b.config_value;
      if (changed) {
        const err = validateRow(a);
        if (err) {
          setStatus({ type: "danger", msg: `${a.label}: ${err}` });
          return;
        }

        updates.push({
          config_name: a.config_name,
          config_value: a.config_value,
        });
      }
    }

    if (updates.length === 0) {
      setStatus({ type: "info", msg: "No changes to save." });
      enableTableEdit(false);
      return;
    }

    try {
      setSaving(true);
      setStatus({ type: "", msg: "" });

      const resp = await setInfAppConfigsAPI({
        updatedBy: loggedInUserId,
        updates,
      });

      // Refresh rows from server truth (recommended)
      await loadConfigs();

      setStatus({ type: "success", msg: resp?.message || `Saved ${updates.length} change(s).` });
      enableTableEdit(false);
    } catch (e) {
      setStatus({
        type: "danger",
        msg: e?.response?.data?.message || e?.message || "Failed to save table.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper overflow-hidden">
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12">
          <ComponentCard>
            <h4 className="muc-title">Other controls</h4>

            {status.msg && (
              <Alert
                color={status.type}
                className="muc-alert"
                isOpen={!!status.msg}
                toggle={() => setStatus({ type: "", msg: "" })}
              >
                {status.msg}
              </Alert>
            )}

            <div className="muc-wrap" style={{ marginTop: 24 }}>
              <Card className="muc-card">
                <CardBody className="muc-card-body">
                  {/* Header actions (match ManageUserCredits style) */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", }}>
                    <div style={{ marginRight: "auto" }}>
                      <Button
                        className="submit-action-button muc-submit-btn"
                        type="button"
                        disabled={loading || saving}
                        onClick={loadConfigs}
                        title="Refresh configs"
                        style={{
                          padding: "5px 5px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {loading ? <Spinner size="sm" /> : <RefreshIcon fontSize="small" />}
                      </Button>
                    </div>

                    <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
                      {!tableEditing ? (
                        <Button
                          size="sm"
                          color="secondary"
                          disabled={rows.length === 0 || loading || saving}
                          onClick={() => enableTableEdit(true)}
                        >
                          Edit Table
                        </Button>
                      ) : (
                        <>
                          <Button
                            className="submit-action-button muc-submit-btn"
                            size="sm"
                            disabled={saving}
                            onClick={handleTableSubmit}
                            title="Submit all changes"
                          >
                            {saving ? (
                              <>
                                <Spinner size="sm" style={{ marginRight: 8 }} /> Saving...
                              </>
                            ) : (
                              "✓ Submit All"
                            )}
                          </Button>
                          <Button
                            className="danger-action-button"
                            size="sm"
                            disabled={saving}
                            onClick={handleTableCancel}
                            title="Cancel all edits"
                          >
                            ✕ Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ marginTop: 20, overflowX: "auto", width: "100%" }}>
                    <Table bordered hover size="md" style={{ width: "100%", minWidth: "700px" }}>
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>Control</th>
                          <th style={{ whiteSpace: "nowrap" }}>Value</th>
                          <th style={{ whiteSpace: "nowrap" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={3} style={{ textAlign: "center", color: "#777" }}>
                              {loading ? "Loading..." : "No data"}
                            </td>
                          </tr>
                        ) : (
                          rows.map((r, idx) => {
                            const rk = rowKey(r);
                            const isEditing = !!rowEditing[rk];

                            return (
                              <tr key={rk}>
                                <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{r.label}</td>

                                <td style={{ maxWidth: 360 }}>
                                  {!isEditing ? (
                                    r.type === "bool" ? (
                                      r.config_value ? "Required" : "Not required"
                                    ) : (
                                      String(r.config_value)
                                    )
                                  ) : r.type === "bool" ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      <Input
                                        type="switch"
                                        checked={!!r.config_value}
                                        disabled={saving}
                                        onChange={(e) => handleCellChange(idx, e.target.checked)}
                                      />
                                      <span style={{ color: "#444" }}>Required</span>
                                    </div>
                                  ) : (
                                    <Input
                                      type="number"
                                      min={0}
                                      step={1}
                                      value={r.config_value}
                                      disabled={saving}
                                      onKeyDown={preventInvalidIntKeys}
                                      onChange={(e) => handleCellChange(idx, e.target.value)}
                                      style={{ maxWidth: 140 }}
                                    />
                                  )}
                                </td>

                                <td style={{ whiteSpace: "nowrap" }}>
                                  {!isEditing ? (
                                    <Button
                                      size="sm"
                                      color="secondary"
                                      onClick={() => enableRowEdit(rk, true)}
                                      disabled={tableEditing || loading || saving}
                                    >
                                      Edit
                                    </Button>
                                  ) : (
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <Button
                                        className="submit-action-button muc-tick-btn"
                                        size="sm"
                                        disabled={saving}
                                        onClick={() => handleRowSubmit(rk)}
                                        title="Submit row"
                                      >
                                        ✓
                                      </Button>
                                      <Button
                                        className="danger-action-button"
                                        size="sm"
                                        disabled={saving}
                                        onClick={() => handleRowCancel(rk)}
                                        title="Cancel row"
                                      >
                                        ✕
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* keeping these as your existing pattern */}
                  <div style={{ marginTop: 10, fontSize: 12, color: "transparent" }}>
                    {defaultOrg} {loggedInUserId}
                  </div>
                </CardBody>
              </Card>
            </div>
          </ComponentCard>
        </Col>
      </Row>
    </div>
  );
}
